import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaLightbulb,
  FaMap,
  FaRocket,
  FaChartLine,
  FaExpand,
  FaCheckCircle,
  FaArrowRight,
  FaWhatsapp,
  FaDatabase,
  FaSyncAlt,
  FaLayerGroup
} from 'react-icons/fa';
import { HiSparkles, HiLightningBolt, HiCube } from 'react-icons/hi';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ProcessPage = () => {
  const [activeStep, setActiveStep] = useState(0);

  // Auto-cycle through steps every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 6);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // WhatsApp configuration
  const whatsappNumber = '918700186979';
  const whatsappMessage = 'Hello! I would like to learn more about your strategic process.';
  const openWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  const processSteps = [
    {
      number: '01',
      icon: FaLightbulb,
      title: 'Brand & Business Understanding',
      description: 'Deep dive into your business goals, target audience, and market position',
      details: ['Business audit', 'Competitor analysis', 'Audience research', 'Goal setting'],
      color: 'from-amber-500 to-orange-600',
      bgColor: 'bg-amber-500',
      lightBg: 'bg-amber-500/10',
    },
    {
      number: '02',
      icon: FaMap,
      title: 'Strategy & Platform Mapping',
      description: 'Identify the right channels and create a comprehensive growth roadmap',
      details: ['Channel selection', 'Content strategy', 'Budget allocation', 'Timeline planning'],
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-500',
      lightBg: 'bg-blue-500/10',
    },
    {
      number: '03',
      icon: HiCube,
      title: 'Creative & Tech Execution',
      description: 'Bring the strategy to life with compelling content and technology',
      details: ['Content creation', 'Design & branding', 'Tech setup', 'Asset production'],
      color: 'from-purple-500 to-violet-600',
      bgColor: 'bg-purple-500',
      lightBg: 'bg-purple-500/10',
    },
    {
      number: '04',
      icon: FaRocket,
      title: 'Launch & Promotion',
      description: 'Execute campaigns across selected platforms with precision',
      details: ['Campaign launch', 'Paid promotion', 'Influencer outreach', 'PR activities'],
      color: 'from-pink-500 to-rose-600',
      bgColor: 'bg-pink-500',
      lightBg: 'bg-pink-500/10',
    },
    {
      number: '05',
      icon: FaChartLine,
      title: 'Tracking & Optimization',
      description: 'Monitor performance and continuously optimize for better results',
      details: ['Analytics tracking', 'A/B testing', 'Performance reports', 'Strategy refinement'],
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-500',
      lightBg: 'bg-green-500/10',
    },
    {
      number: '06',
      icon: FaExpand,
      title: 'Scale & Long-term Growth',
      description: 'Expand successful strategies and build sustainable growth systems',
      details: ['Scale winners', 'New markets', 'Automation', 'Long-term planning'],
      color: 'from-cyan-500 to-teal-600',
      bgColor: 'bg-cyan-500',
      lightBg: 'bg-cyan-500/10',
    },
  ];

  const whyItWorks = [
    {
      icon: FaDatabase,
      title: 'Data-Driven Decisions',
      description: 'Every step is backed by analytics and market research, not guesswork',
      color: 'from-blue-500 to-indigo-600',
    },
    {
      icon: FaSyncAlt,
      title: 'Continuous Optimization',
      description: "We don't set and forget. Regular testing and refinement ensures peak performance",
      color: 'from-green-500 to-emerald-600',
    },
    {
      icon: FaLayerGroup,
      title: 'Scalable Framework',
      description: 'Built for growth. As your business expands, our strategies scale with you',
      color: 'from-purple-500 to-violet-600',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 50, repeat: Infinity, ease: 'linear' }}
            className="absolute -top-1/2 -left-1/2 w-full h-full"
          >
            <div className="absolute top-1/3 left-1/3 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-[150px]" />
            <div className="absolute bottom-1/3 right-1/3 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[150px]" />
          </motion.div>

          {/* Floating Elements */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${15 + i * 15}%`,
                top: `${20 + (i % 3) * 25}%`,
              }}
              animate={{
                y: [-20, 20, -20],
                rotate: [0, 10, -10, 0],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 4 + i,
                repeat: Infinity,
                delay: i * 0.5,
              }}
            >
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${processSteps[i].color} opacity-20 blur-sm`} />
            </motion.div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8"
            >
              <HiLightningBolt className="text-yellow-400 text-lg" />
              <span className="text-white font-semibold text-sm">Our Process</span>
            </motion.div>

            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
            >
              Our Strategic{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
                Process
              </span>
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto"
            >
              A proven 6-step methodology that transforms businesses into digital success stories
            </motion.p>

            {/* Process Preview Icons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex justify-center gap-3 mt-10 mb-16"
            >
              {processSteps.map((step, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.2, y: -5 }}
                  onClick={() => setActiveStep(index)}
                  className={`w-12 h-12 rounded-xl flex items-center justify-center cursor-pointer transition-all duration-300 ${activeStep === index
                      ? `bg-gradient-to-br ${step.color} shadow-lg`
                      : 'bg-white/10 hover:bg-white/20'
                    }`}
                >
                  <step.icon className="text-white text-lg" />
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom Wave */}
        <div className="absolute -bottom-1 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-16">
            <path d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,64C960,75,1056,85,1152,80C1248,75,1344,53,1392,42.7L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Interactive Process Timeline */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-indigo-100/50 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-purple-100/50 to-transparent rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-full text-indigo-600 text-sm font-semibold mb-4">
              Step by Step
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              How We{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                Transform
              </span>{' '}
              Businesses
            </h2>
          </motion.div>

          {/* Main Process Display */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Process Steps List */}
            <div className="space-y-4">
              {processSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setActiveStep(index)}
                  className={`relative cursor-pointer group`}
                >
                  <div className={`p-5 rounded-2xl transition-all duration-500 ${activeStep === index
                      ? `bg-gradient-to-r ${step.color} shadow-xl`
                      : 'bg-slate-50 hover:bg-slate-100'
                    }`}>
                    <div className="flex items-center gap-4">
                      {/* Step Number */}
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center font-bold text-xl transition-all ${activeStep === index
                          ? 'bg-white/20 text-white'
                          : `bg-gradient-to-br ${step.color} text-white`
                        }`}>
                        {step.number}
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <h3 className={`font-bold text-lg transition-colors ${activeStep === index ? 'text-white' : 'text-slate-900'
                          }`}>
                          {step.title}
                        </h3>
                        <p className={`text-sm transition-colors ${activeStep === index ? 'text-white/80' : 'text-slate-500'
                          }`}>
                          {step.description}
                        </p>
                      </div>

                      {/* Arrow */}
                      <FaArrowRight className={`transition-all ${activeStep === index
                          ? 'text-white translate-x-1'
                          : 'text-slate-400 group-hover:translate-x-1'
                        }`} />
                    </div>
                  </div>

                  {/* Connector Line */}
                  {index < processSteps.length - 1 && (
                    <div className="absolute left-7 top-full w-0.5 h-4 bg-slate-200" />
                  )}
                </motion.div>
              ))}
            </div>

            {/* Right: Active Step Detail */}
            <div className="lg:sticky lg:top-32">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -20 }}
                  transition={{ duration: 0.4 }}
                  className={`relative bg-gradient-to-br ${processSteps[activeStep].color} rounded-3xl p-8 md:p-10 shadow-2xl overflow-hidden`}
                >
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                      backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                      backgroundSize: '24px 24px'
                    }} />
                  </div>

                  {/* Floating Icon */}
                  <motion.div
                    animate={{ y: [-10, 10, -10], rotate: [-5, 5, -5] }}
                    transition={{ duration: 5, repeat: Infinity }}
                    className="absolute top-6 right-6 w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center"
                  >
                    {React.createElement(processSteps[activeStep].icon, { className: 'text-4xl text-white' })}
                  </motion.div>

                  <div className="relative z-10">
                    {/* Step Number */}
                    <span className="text-8xl font-black text-white/20 absolute -top-4 -left-2">
                      {processSteps[activeStep].number}
                    </span>

                    <div className="pt-12">
                      <h3 className="text-3xl font-bold text-white mb-4">
                        {processSteps[activeStep].title}
                      </h3>
                      <p className="text-white/90 text-lg mb-8 leading-relaxed">
                        {processSteps[activeStep].description}
                      </p>

                      {/* Details List */}
                      <div className="space-y-3 mb-8">
                        {processSteps[activeStep].details.map((detail, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                            className="flex items-center gap-3"
                          >
                            <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                              <FaCheckCircle className="text-white text-xs" />
                            </div>
                            <span className="text-white font-medium">{detail}</span>
                          </motion.div>
                        ))}
                      </div>

                      {/* Progress Indicator */}
                      <div className="flex items-center gap-2">
                        <span className="text-white/70 text-sm">Step {activeStep + 1} of 6</span>
                        <div className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${((activeStep + 1) / 6) * 100}%` }}
                            transition={{ duration: 0.5 }}
                            className="h-full bg-white rounded-full"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Process Flow */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              The{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                Journey
              </span>
            </h2>
            <p className="text-slate-600 text-lg">From strategy to success, visualized</p>
          </motion.div>

          {/* Horizontal Process Flow */}
          <div className="relative">
            {/* Connection Line */}
            <div className="absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-purple-500 to-cyan-500 hidden lg:block" />

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6"
            >
              {processSteps.map((step, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ y: -10 }}
                  className="group"
                >
                  <div className="relative">
                    {/* Step Card */}
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 group-hover:shadow-xl group-hover:border-transparent transition-all duration-300 text-center">
                      {/* Icon */}
                      <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                        <step.icon className="text-2xl text-white" />
                      </div>

                      {/* Number Badge */}
                      <div className={`absolute -top-3 -right-3 w-8 h-8 bg-gradient-to-br ${step.color} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                        {index + 1}
                      </div>

                      {/* Title */}
                      <h4 className="font-bold text-slate-900 text-sm leading-tight">
                        {step.title}
                      </h4>
                    </div>

                    {/* Arrow (hidden on last item and mobile) */}
                    {index < processSteps.length - 1 && (
                      <div className="absolute top-1/2 -right-4 transform -translate-y-1/2 hidden lg:block z-10">
                        <FaArrowRight className="text-slate-300" />
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why This Process Works */}
      <section className="py-20 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-[150px]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-indigo-400 text-sm font-semibold mb-4">
              The Difference
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why This Process{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                Works
              </span>
            </h2>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            {whyItWorks.map((item, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                className="group relative"
              >
                {/* Glow Effect */}
                <div className={`absolute -inset-1 bg-gradient-to-r ${item.color} rounded-3xl opacity-0 group-hover:opacity-50 blur-xl transition-all duration-500`} />

                <div className="relative bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 group-hover:border-white/20 transition-all h-full">
                  {/* Number */}
                  <span className="absolute top-6 right-6 text-6xl font-black text-white/5">
                    {index + 1}
                  </span>

                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                    <item.icon className="text-2xl text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-indigo-500/5 via-purple-500/5 to-pink-500/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <motion.div
              animate={{ y: [-5, 5, -5] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-full border border-indigo-500/20 mb-8"
            >
              <HiSparkles className="text-indigo-600" />
              <span className="text-indigo-700 font-medium">Ready to Start?</span>
            </motion.div>

            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Let's Build Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                Success Story
              </span>
            </h2>
            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
              Start your transformation journey with our proven 6-step process
            </p>

            <motion.button
              onClick={openWhatsApp}
              whileHover={{ scale: 1.05, boxShadow: '0 25px 50px -12px rgba(99, 102, 241, 0.4)' }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-lg rounded-full shadow-2xl transition-all cursor-pointer group"
            >
              <FaWhatsapp className="text-2xl" />
              Start Your Journey
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <FaArrowRight className="group-hover:translate-x-2 transition-transform" />
              </motion.span>
            </motion.button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProcessPage;
